/**
 * CommentCard — Displays a single voice comment in the feedback panel.
 *
 * Simplified view showing: page title, recording duration, LLM summary,
 * and a delete button. No inline editing — editing happens at the
 * proposal review stage.
 */
import type { VoiceComment } from "../../types/feedback";

interface CommentCardProps {
  readonly comment: VoiceComment;
  readonly onDelete: (index: number) => void;
}

export function CommentCard({ comment, onDelete }: CommentCardProps) {
  const routes = Array.from(
    new Set(
      comment.screenCaptures.map((capture) => capture.route).filter(Boolean)
    )
  );
  const minutes = Math.floor(comment.durationSeconds / 60);
  const seconds = comment.durationSeconds % 60;
  const durationText = `${minutes}:${seconds.toString().padStart(2, "0")}`;

  return (
    <div className="fb-comment-card">
      <div className="fb-comment-header">
        <span className="fb-comment-index">#{comment.index}</span>
        <span className="fb-comment-duration">{durationText}</span>
        <button
          type="button"
          className="fb-comment-delete"
          onClick={() => onDelete(comment.index)}
          aria-label={`Delete comment ${comment.index}`}
          title="Delete comment"
        >
          ✕
        </button>
      </div>

      {/* Summary */}
      <div className="fb-comment-summary">
        {comment.summary ?? <em>Summarizing&hellip;</em>}
      </div>

      {/* Route pins */}
      {routes.length > 0 && (
        <div className="fb-comment-routes" aria-label="Captured routes">
          {routes.map((route) => (
            <span key={route} className="fb-comment-route-pill">
              {route}
            </span>
          ))}
        </div>
      )}

      {/* Status */}
      {comment.transcriptionStatus === "transcribing" && (
        <span className="fb-comment-status">Transcribing&hellip;</span>
      )}
      {comment.transcriptionStatus === "failed" && (
        <span className="fb-comment-status fb-comment-status--error">
          Transcription failed
        </span>
      )}
    </div>
  );
}
